/**
 * NorthHoustonBudget — 100% self-contained Proposal 02 budget.
 *
 * No DeploymentContext, no shared data with Baytown's Budget.jsx.
 * The only data import is the NH-specific data lib. This component
 * renders ONLY North Houston District numbers
 * (1,200 households · $3,455,560 Year-1 · 3 Spokes × 400 each).
 *
 * Reason: To guarantee zero cross-deployment data bleed, this is a
 * physically separate component from the Baytown Budget.
 */
import { useState } from "react";
import axios from "axios";
import {
  Lock, ShieldCheck, ArrowRight, Loader2, Network, Building2,
  TrendingUp, Users, ScrollText, FileSpreadsheet, Briefcase,
  FileCheck2, Cog, Rocket, Landmark, PiggyBank, GitMerge,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  nhCentralHubLines,
  nhAgencyBudgetLines,
  nhPortfolioContracts,
  nhSystemTotals,
  nhFiveYearForecast,
  nhFiveYearGrandTotal,
  nhFiveYearAllInTotal,
  nhTriPortfolioROI,
  nhOperationalPhases,
} from "@/lib/northHoustonData";
import { generateNHBudgetSpreadsheet } from "@/lib/nhSpreadsheetGenerator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "nhd_financials_unlocked";
const fmt = (n) => `$${n.toLocaleString()}`;
const phaseIcons = [FileCheck2, FileCheck2, Cog, Rocket];

// Developer Group taper (NH 5-year schedule)
const devGroup = {
  team: [
    { role: "Principal Consultant & Program Creator", who: "George Broughton III · Sterile Mate LLC", detail: "Founding Architect of the model. Leads development, train-the-trainer, and 2-year handoff.", cost: 135000 },
    { role: "LCSW · Clinical Curriculum Lead", who: "Sub-contracted Houston-area LCSW", detail: "Trauma-informed care curriculum, clinical supervision protocols, intake matrix design.", cost: 95000 },
    { role: "Senior Living Social Worker", who: "Sub-contracted Texas-licensed senior-care SW", detail: "Senior Care Pipeline curriculum: aging-in-place, AAA navigation, in-home care coordination.", cost: 85000 },
    { role: "Employment Specialist", who: "Sub-contracted workforce-development expert", detail: "Trade Pipeline curriculum: Lone Star North Harris, HCC North, Workforce Solutions, Amazon Career Choice.", cost: 75000 },
    { role: "Continuing Education Specialist", who: "Sub-contracted adult-ed / training designer", detail: "Builds the connector recertification pipeline, manages the train-the-trainer materials library.", cost: 75000 },
  ],
  contract: [
    { period: "Year 1 (Firm)", phase: "Develop · Build · Train", cost: 515000, detail: "Months 1–6: program development (curriculum, matrices, data platform). Months 7–12: train-the-trainer rollout to the Hub and all three Spoke agencies.", highlight: true },
    { period: "Year 2 (Firm)", phase: "Stabilize · Audit · Hand off", cost: 257500, detail: "50% engagement. Quarterly fidelity audits, connector recertification, agency-supervisor coaching." },
    { period: "Year 3 (Option)", phase: "Supervised Independence", cost: 125000, detail: "25% engagement at the District's option. Spot reviews, annual recertification refresh, problem-case consultation only." },
    { period: "Year 4 (Option)", phase: "Advisory Only", cost: 75000, detail: "Quarterly advisory retainer at the District's option. NH executive team owns operations." },
    { period: "Year 5 (Option)", phase: "Advisory Only", cost: 50000, detail: "Annual advisory check-in at the District's option. Full NH independence achieved." },
  ],
  totals: { year1: 515000, year2: 257500, twoYearFirm: 772500, fiveYearAllOptions: 1022500, teamSalariesYr1: 465000, directExpensesYr1: 50000 },
};

// ============ BUDGET CONTENT (unlocked view) ============
function NHBudgetUnlocked() {
  return (
    <section
      id="budget"
      data-testid="section-budget-nh"
      className="bg-white py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Proposal Status Disclaimer */}
        <div data-testid="nh-budget-disclaimer" className="mb-8 border-l-4 border-[#D4AF37] bg-[#FEF8E7] px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B6914] whitespace-nowrap mt-0.5">
              ⚠ Proposed Budget
            </span>
            <p className="text-xs text-[#5C4A0A] leading-relaxed">
              <strong>Illustrative budget developed by Sterile Mate LLC for discussion with North Houston District leadership.</strong>{" "}
              Figures are subject to negotiation and have not been adopted by the North Houston District Board, Houston City Council, Harris County Commissioners Court, or any funding agency. Not a binding offer. Document version: <em>Proposal v1.0</em>.
            </p>
          </div>
        </div>

        {/* Scope Badge */}
        <div data-testid="budget-nh-badge" className="mb-8 inline-flex items-center gap-3 bg-[#0A192F] text-[#F9F7F1] px-4 py-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#D4AF37]">Budget Scope</span>
          <span className="font-mono text-[10px] uppercase tracking-widest">
            Proposal 02 · North Houston District · 1,200 Households · 3 Spokes × 400 · ~80% of Baytown baseline
          </span>
        </div>

        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                The Tri-Portfolio Framework · 5-Year Multi-Agency Model
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              One District. Three agencies.<br />
              <em className="italic">1,200 households a year.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#334155] leading-relaxed">
            <p>
              The North Houston District acts as Master Administrator — the <strong>Hub</strong> — while three competitively-selected nonprofit service providers operate as <strong>Spokes</strong>, each running a distinct 400-household portfolio. Five-year contracts with annual performance review per agency. One standardized data platform across the entire system.
            </p>
          </div>
        </div>

        {/* Headline ribbon */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-12">
          {[
            { k: fmt(nhSystemTotals.grandTotal), v: "Total Annual Regional Investment (Yr 1)", accent: "#0A192F" },
            { k: nhSystemTotals.householdsServed.toLocaleString(), v: "Households Served Annually", accent: "#0A192F" },
            { k: fmt(nhSystemTotals.costPerHousehold), v: "Cost / Household / Year (~$240/mo)", accent: "#8B0000" },
            { k: `1 : ${nhSystemTotals.caseload}`, v: "Service-Connector Caseload Cap", accent: "#0A192F" },
          ].map((s, i) => (
            <div key={i} data-testid={`nh-budget-headline-${i}`} className="bg-white p-6 lg:p-7">
              <div className="font-display text-3xl lg:text-4xl" style={{ color: s.accent }}>{s.k}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-2 leading-snug">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Spreadsheet Download CTA */}
        <div data-testid="nh-budget-spreadsheet-cta" className="mb-16 bg-[#0A192F] text-[#F9F7F1] p-5 lg:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-15 pointer-events-none" />
          <div className="relative z-10 flex items-start gap-4">
            <FileSpreadsheet className="h-6 w-6 text-[#D4AF37] shrink-0 mt-1" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                For District Board, Finance &amp; RFP Review
              </div>
              <div className="font-display text-lg lg:text-xl leading-tight">
                Download the complete 5-year financials as an Excel workbook.
              </div>
              <p className="text-xs text-[#CBD5E1] mt-1 max-w-2xl">
                13 sheets: Executive Summary · 5-Year Forecast · Program Goals · Outcome Benchmarks · Hub Budget · Per-Agency Budget · Developer Group · Tri-Portfolio · Funding Sources · Regional Hub Scenarios · Launch Plan · ROI · Partners.
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="nh-download-spreadsheet-btn"
            onClick={generateNHBudgetSpreadsheet}
            className="relative z-10 inline-flex items-center justify-center gap-2 bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] h-12 px-6 font-mono text-xs uppercase tracking-widest whitespace-nowrap transition-colors"
          >
            Download .xlsx <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Hub-and-Spokes diagram */}
        <div data-testid="nh-budget-hub-spokes" className="grid lg:grid-cols-12 gap-4 mb-16">
          <div className="lg:col-span-4 bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-8 relative overflow-hidden flex flex-col justify-between min-h-[280px]">
            <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
            <div className="relative z-10">
              <Network className="h-7 w-7 text-[#D4AF37] mb-4" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">The Hub</div>
              <div className="font-display text-2xl lg:text-3xl leading-tight">
                North Houston District<br />Central Coordination Unit
              </div>
            </div>
            <div className="relative z-10 mt-6 pt-5 border-t border-[#F9F7F1]/20">
              <div className="font-display text-3xl text-[#F9F7F1]">{fmt(nhSystemTotals.centralHub)}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#CBD5E1] mt-1">
                Executive team, clinical oversight, field audit
              </div>
            </div>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-3">
            {nhPortfolioContracts.map((p) => (
              <div key={p.id} data-testid={`nh-portfolio-${p.id}`} className="bg-[#F9F7F1] border border-[#CBD5E1] p-5 lg:p-6 flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <span className="grid place-items-center w-10 h-10 border" style={{ borderColor: p.color, color: p.color }}>
                    <Building2 className="h-4 w-4" />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: p.color }}>
                    Spoke 0{p.id}
                  </span>
                </div>
                <h4 className="font-display text-base text-[#0A192F] leading-snug">{p.name}</h4>
                <p className="mt-2 text-xs text-[#475569] leading-relaxed flex-1">{p.focus}</p>
                <div className="mt-4 pt-3 border-t border-[#CBD5E1] flex items-baseline justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#64748B]">{p.households} households</span>
                  <span className="font-display text-lg text-[#0A192F]">{fmt(p.cost)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Operational Phases */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-[#8B0000]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
              Multi-Agency Operational Plan · Year-One Launch
            </span>
          </div>
          <h3 className="font-display text-2xl lg:text-4xl text-[#0A192F] leading-tight mb-8">
            From Board resolution to 1,200 households in <em className="italic">six months</em>.
          </h3>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            {nhOperationalPhases.map((p, i) => {
              const Icon = phaseIcons[i] || FileCheck2;
              return (
                <li key={p.title} data-testid={`nh-phase-${i}`} className="bg-white p-6 lg:p-7 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <Icon className="h-6 w-6 text-[#8B0000]" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
                      {p.period}
                    </span>
                  </div>
                  <h4 className="font-display text-base text-[#0A192F] leading-snug">{p.title}</h4>
                  <p className="mt-3 text-xs text-[#475569] leading-relaxed flex-1">{p.body}</p>
                </li>
              );
            })}
          </ol>
        </div>

        {/* === Funding Sources Stack — where the dollars come from === */}
        <div data-testid="nh-funding-sources" className="bg-[#0A192F] text-[#F9F7F1] mb-16 relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-20 pointer-events-none" />
          <div className="relative z-10 p-7 lg:p-10">
            <div className="grid lg:grid-cols-12 gap-8 mb-8">
              <div className="lg:col-span-5">
                <Landmark className="h-7 w-7 text-[#D4AF37] mb-3" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                  Funding Stack · Where the {fmt(nhSystemTotals.grandTotal)} Comes From
                </div>
                <h3 className="font-display text-2xl lg:text-3xl text-[#F9F7F1] leading-tight">
                  A six-source federal &amp; regional stack — <em className="italic text-[#D4AF37]">no single point of failure</em>.
                </h3>
                <p className="mt-4 text-sm text-[#CBD5E1] leading-relaxed">
                  Every dollar is an already-appropriated public source or a high-probability matching philanthropic dollar. Bridge does not invent new funding lines — it activates capital that is already on the books and currently underutilized for this cohort.
                </p>
              </div>
              <ul className="lg:col-span-7 space-y-3">
                {[
                  { src: "HUD CDBG · Houston + Harris County entitlement", pct: 40, amt: 1382224, note: "Public Services for Vulnerable Populations (eligible CDBG activity) — primary anchor. Section 108 loan capacity available for multi-year commitment.", color: "#D4AF37" },
                  { src: "Anchor Employer + Philanthropic Match", pct: 20, amt: 691112, note: "Houston Endowment, Kinder Foundation, JPMorgan PolicyCenter, Amazon Career Choice, Sysco corporate, FedEx Cares — funders prefer matching public dollars 1:1.", color: "#F9F7F1" },
                  { src: "Workforce Solutions Greater Houston (WIOA)", pct: 15, amt: 518334, note: "Already-appropriated WIOA Title I + Childcare Subsidy dollars — pays for the trade-pipeline, CDL training, and childcare-during-training portions directly.", color: "#CBD5E1" },
                  { src: "Harris County ARPA / General Fund", pct: 12, amt: 414667, note: "Commissioners Court has appropriated similar service-coordination dollars previously (Public Health, Justice Administration). Precinct 1 is the natural champion.", color: "#94A3B8" },
                  { src: "HUD ESG / Continuum of Care", pct: 8, amt: 276445, note: "Eviction-prevention + housing-stability components are explicitly ESG/CoC eligible. Coalition for the Homeless of Houston/Harris County is the pass-through.", color: "#64748B" },
                  { src: "TIRZ #11 · Greater Greenspoint Redevelopment Authority", pct: 5, amt: 172778, note: "District-bounded community-development dollars — local match that strengthens every federal application.", color: "#475569" },
                ].map((f, i) => (
                  <li key={f.src} data-testid={`nh-funding-source-${i}`} className="border border-[#F9F7F1]/15 bg-[#F9F7F1]/5 p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                      <span className="font-display text-base text-[#F9F7F1] leading-snug">{f.src}</span>
                      <span className="font-mono text-xs text-[#D4AF37] whitespace-nowrap">~{f.pct}% · {fmt(f.amt)}</span>
                    </div>
                    <div className="h-1 bg-[#F9F7F1]/10 overflow-hidden">
                      <div className="h-full" style={{ width: `${f.pct * 2.4}%`, backgroundColor: f.color }} />
                    </div>
                    <p className="text-[11px] text-[#CBD5E1] mt-2 leading-relaxed">{f.note}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-[#F9F7F1]/15 pt-5 grid sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Federal Share</div>
                <div className="font-display text-2xl text-[#F9F7F1] mt-1">~63%</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mt-1">CDBG + WIOA + ESG/CoC</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Regional &amp; County</div>
                <div className="font-display text-2xl text-[#F9F7F1] mt-1">~17%</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mt-1">ARPA + TIRZ</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Private Match</div>
                <div className="font-display text-2xl text-[#F9F7F1] mt-1">~20%</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] mt-1">Anchor + Philanthropic</div>
              </div>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#94A3B8] mt-5 leading-relaxed">
              Illustrative funding-mix scenario based on the eligible activities and the published appropriation patterns of each source. Final mix locked in during the District Board resolution and grant-writing phase (Months 1–3).
            </p>
          </div>
        </div>

        {/* === Part A: Central Hub Budget detail === */}
        <div data-testid="nh-budget-central-hub" className="bg-[#F9F7F1] border border-[#CBD5E1] mb-6">
          <div className="px-6 lg:px-8 py-5 border-b border-[#CBD5E1] flex items-baseline justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                Part A · The District Hub
              </div>
              <div className="font-display text-xl text-[#0A192F]">
                Central Coordination Unit Infrastructure
              </div>
            </div>
            <div className="font-display text-2xl text-[#0A192F] whitespace-nowrap">
              {fmt(nhSystemTotals.centralHub)}
            </div>
          </div>
          <ul className="divide-y divide-[#CBD5E1]">
            {nhCentralHubLines.map((l, i) => (
              <li key={l.line} data-testid={`nh-hub-line-${i}`} className="px-6 lg:px-8 py-4 grid grid-cols-12 gap-4 items-baseline">
                <div className="col-span-9">
                  <div className="font-display text-base text-[#0A192F]">{l.line}</div>
                  <div className="text-xs text-[#475569] mt-0.5">{l.detail}</div>
                </div>
                <div className="col-span-3 text-right font-display text-base text-[#0A192F]">{fmt(l.cost)}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* === Regional Hub Consolidation callout === */}
        <div data-testid="nh-regional-hub" className="bg-[#F9F7F1] border border-[#D4AF37] mb-16 relative overflow-hidden">
          <div className="p-7 lg:p-10">
            <div className="grid lg:grid-cols-12 gap-8 items-start mb-7">
              <div className="lg:col-span-5">
                <GitMerge className="h-7 w-7 text-[#8B0000] mb-3" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                  Scaling Note · Regional Hub Consolidation
                </div>
                <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F] leading-tight">
                  When the second deployment goes live, this Hub becomes <em className="italic text-[#8B0000]">a shared Harris County resource</em>.
                </h3>
                <p className="mt-4 text-sm text-[#334155] leading-relaxed">
                  The {fmt(nhSystemTotals.centralHub)} Hub above is sized for the single-deployment Year-1 launch. As soon as a 2nd or 3rd Harris County deployment goes live (Baytown, Pasadena, Aldine, etc.), the same Central Coordination Unit supervises all of them — a modest staff bump (~$100k for additional Field Manager coverage) is the only marginal cost.
                </p>
                <p className="mt-3 text-sm text-[#334155] leading-relaxed">
                  This is how HUD and grant reviewers expect overhead to behave: <strong className="text-[#0A192F]">cost-per-household for central coordination drops dramatically as deployments multiply.</strong>
                </p>
              </div>
              <div className="lg:col-span-7 space-y-3">
                <PiggyBank className="h-7 w-7 text-[#8B0000] mb-1" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-2">
                  Hub Cost Allocation · Per Household · Across Scenarios
                </div>
                {[
                  { label: "1 Deployment", deployments: 1, households: 1200, hubTotal: 706060, perDeploymentAsk: 3455560, highlight: false, note: "Today's NH Year-1 launch (this proposal)" },
                  { label: "2 Deployments", deployments: 2, households: 2700, hubTotal: 810000, perDeploymentAsk: 3154500, highlight: true, note: "Add Baytown (1,500 HH). Hub grows ~15%, supervises both." },
                  { label: "3 Deployments", deployments: 3, households: 4200, hubTotal: 920000, perDeploymentAsk: 3056167, highlight: false, note: "Add Aldine/Pasadena (1,500 HH). Hub at ~$307k per deployment." },
                  { label: "4 Deployments", deployments: 4, households: 5700, hubTotal: 1040000, perDeploymentAsk: 3009500, highlight: false, note: "County-wide. Hub at ~$260k per deployment. Per-HH cost = $182." },
                ].map((s, i) => {
                  const perHH = Math.round(s.hubTotal / s.households);
                  const hubShare = Math.round(s.hubTotal / s.deployments);
                  return (
                    <div
                      key={s.label}
                      data-testid={`nh-regional-scenario-${i}`}
                      className={`p-4 grid grid-cols-12 gap-3 items-baseline border ${s.highlight ? "bg-[#0A192F] text-[#F9F7F1] border-[#D4AF37]" : "bg-white border-[#CBD5E1]"}`}
                    >
                      <div className="col-span-12 sm:col-span-3">
                        <div className={`font-display text-lg ${s.highlight ? "text-[#D4AF37]" : "text-[#0A192F]"}`}>{s.label}</div>
                        <div className={`font-mono text-[10px] uppercase tracking-widest mt-0.5 ${s.highlight ? "text-[#CBD5E1]" : "text-[#475569]"}`}>
                          {s.households.toLocaleString()} HH
                        </div>
                      </div>
                      <div className="col-span-4 sm:col-span-3 text-right sm:text-left">
                        <div className={`font-display text-base ${s.highlight ? "text-[#F9F7F1]" : "text-[#0A192F]"}`}>{fmt(s.hubTotal)}</div>
                        <div className={`font-mono text-[10px] uppercase tracking-widest mt-0.5 ${s.highlight ? "text-[#94A3B8]" : "text-[#475569]"}`}>
                          Total Hub Cost
                        </div>
                      </div>
                      <div className="col-span-4 sm:col-span-3 text-right sm:text-left">
                        <div className={`font-display text-base ${s.highlight ? "text-[#F9F7F1]" : "text-[#0A192F]"}`}>{fmt(hubShare)}</div>
                        <div className={`font-mono text-[10px] uppercase tracking-widest mt-0.5 ${s.highlight ? "text-[#94A3B8]" : "text-[#475569]"}`}>
                          Hub / Deployment
                        </div>
                      </div>
                      <div className="col-span-4 sm:col-span-3 text-right">
                        <div className={`font-display text-base ${s.highlight ? "text-[#D4AF37]" : "text-[#8B0000]"}`}>${perHH}</div>
                        <div className={`font-mono text-[10px] uppercase tracking-widest mt-0.5 ${s.highlight ? "text-[#94A3B8]" : "text-[#475569]"}`}>
                          Hub / HH / Yr
                        </div>
                      </div>
                      <div className={`col-span-12 text-xs mt-1 leading-relaxed ${s.highlight ? "text-[#CBD5E1]" : "text-[#475569]"}`}>
                        {s.note}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="border-t border-[#CBD5E1] pt-5 grid md:grid-cols-2 gap-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-2">
                  What This Means for the District&apos;s Ask
                </div>
                <p className="text-sm text-[#334155] leading-relaxed">
                  If Harris County opts to fund the Hub <strong className="text-[#0A192F]">centrally</strong> as a County General-Fund or CDBG County-Allocation line item, the North Houston District&apos;s direct ask drops to <strong className="text-[#0A192F]">{fmt(nhSystemTotals.totalAgencies)}</strong> — just the three Spokes. This is the structurally cleanest model for federal grant approval and is how parallel programs (e.g., Maryland&apos;s &quot;Open Doors,&quot; LA County&apos;s &quot;Pathway Home&quot;) are funded today.
                </p>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-2">
                  Recommended Path
                </div>
                <p className="text-sm text-[#334155] leading-relaxed">
                  Launch NH Proposal 02 with the <strong className="text-[#0A192F]">full single-deployment Hub embedded</strong> ({fmt(nhSystemTotals.grandTotal)} Year-1 ask). At the Year-2 contract renewal — once Baytown Proposal 01 is in flight — formally migrate the Hub to a Harris County Regional Coordination Unit and re-baseline both deployments&apos; per-Spoke contracts. Council and the District Board approve the consolidation in concert.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* === Part B: Per-Agency Contract detail === */}
        <div data-testid="nh-budget-per-agency" className="bg-[#0A192F] text-[#F9F7F1] mb-16 relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-20 pointer-events-none" />
          <div className="relative z-10">
            <div className="px-6 lg:px-8 py-5 border-b border-[#F9F7F1]/15 flex flex-col md:flex-row md:items-baseline md:justify-between gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                  Part B · Per-Agency Contract (× 3 Spokes)
                </div>
                <div className="font-display text-xl">
                  Service Provider Operating Budget · 400 Households
                </div>
              </div>
              <div className="font-display text-2xl whitespace-nowrap">
                {fmt(nhSystemTotals.perAgency)} <span className="text-[#D4AF37] text-base">/ agency</span>
              </div>
            </div>
            <ul className="divide-y divide-[#F9F7F1]/15">
              {nhAgencyBudgetLines.map((l, i) => (
                <li key={l.role} data-testid={`nh-agency-line-${i}`} className="px-6 lg:px-8 py-4 grid grid-cols-12 gap-4 items-baseline">
                  <div className="col-span-8 lg:col-span-7">
                    <div className="font-display text-base text-[#F9F7F1]">{l.role}</div>
                    <div className="text-xs text-[#CBD5E1] mt-0.5">{l.detail}</div>
                  </div>
                  <div className="hidden lg:block lg:col-span-2 text-center font-mono text-xs text-[#94A3B8]">
                    {l.fte} FTE
                  </div>
                  <div className="col-span-4 lg:col-span-3 text-right font-display text-base text-[#F9F7F1]">{fmt(l.cost)}</div>
                </li>
              ))}
            </ul>
            <div className="px-6 lg:px-8 py-5 border-t border-[#F9F7F1]/15 grid sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">3 Agencies × {fmt(nhSystemTotals.perAgency)}</div>
                <div className="font-display text-2xl text-[#F9F7F1] mt-1">{fmt(nhSystemTotals.totalAgencies)}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">+ Central Hub</div>
                <div className="font-display text-2xl text-[#F9F7F1] mt-1">{fmt(nhSystemTotals.centralHub)}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Total Annual System</div>
                <div className="font-display text-2xl text-[#D4AF37] mt-1">{fmt(nhSystemTotals.grandTotal)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* === Part C: Developer Group · Sterile Mate LLC === */}
        <div data-testid="nh-budget-developer-group" className="bg-[#F9F7F1] border border-[#CBD5E1] mb-16">
          <div className="px-6 lg:px-8 py-5 border-b border-[#CBD5E1] flex flex-col md:flex-row md:items-baseline md:justify-between gap-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                Part C · Sterile Mate Developer Group
              </div>
              <div className="font-display text-xl text-[#0A192F]">
                2-Year Firm Build + 3-Year District Options
              </div>
              <p className="text-xs text-[#475569] mt-1 max-w-2xl leading-relaxed">
                A separate, time-limited consulting engagement to develop curriculum, intake matrices, the data platform, and to train-the-trainer. Designed to make the North Houston District operationally independent of Sterile Mate by Year 3.
              </p>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">2-Year Firm</div>
              <div className="font-display text-2xl text-[#0A192F]">{fmt(devGroup.totals.twoYearFirm)}</div>
            </div>
          </div>

          {/* Team roster */}
          <div className="px-6 lg:px-8 py-5 border-b border-[#CBD5E1]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-3 flex items-center gap-2">
              <Briefcase className="h-3.5 w-3.5" /> Year 1 Team Roster
            </div>
            <ul className="divide-y divide-[#CBD5E1]">
              {devGroup.team.map((m, i) => (
                <li key={m.role} data-testid={`nh-dev-team-${i}`} className="py-3 grid grid-cols-12 gap-4 items-baseline">
                  <div className="col-span-9">
                    <div className="font-display text-base text-[#0A192F]">{m.role}</div>
                    <div className="text-xs text-[#64748B] mt-0.5"><em>{m.who}</em> · {m.detail}</div>
                  </div>
                  <div className="col-span-3 text-right font-display text-base text-[#0A192F]">{fmt(m.cost)}</div>
                </li>
              ))}
              <li className="py-3 grid grid-cols-12 gap-4 items-baseline border-t border-[#0A192F]/15 mt-2">
                <div className="col-span-9 font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">+ Direct Expenses (curriculum, materials, travel)</div>
                <div className="col-span-3 text-right font-display text-base text-[#0A192F]">{fmt(devGroup.totals.directExpensesYr1)}</div>
              </li>
              <li className="py-3 grid grid-cols-12 gap-4 items-baseline bg-[#0A192F] text-[#F9F7F1] px-3 -mx-3">
                <div className="col-span-9 font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">Year 1 Developer Group Total</div>
                <div className="col-span-3 text-right font-display text-base text-[#D4AF37]">{fmt(devGroup.totals.year1)}</div>
              </li>
            </ul>
          </div>

          {/* Contract schedule */}
          <div className="px-6 lg:px-8 py-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-3">
              Multi-Year Contract Schedule
            </div>
            <ol className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
              {devGroup.contract.map((c, i) => (
                <li key={c.period} data-testid={`nh-dev-contract-${i}`} className={`p-4 flex flex-col ${c.highlight ? "bg-white" : "bg-[#F9F7F1]"}`}>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">{c.period}</div>
                  <div className="font-display text-sm text-[#0A192F] mt-1">{c.phase}</div>
                  <div className="font-display text-lg text-[#0A192F] mt-2">{fmt(c.cost)}</div>
                  <p className="text-[10px] text-[#475569] mt-2 leading-snug flex-1">{c.detail}</p>
                </li>
              ))}
            </ol>
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#64748B] mt-4 leading-relaxed">
              5-Year Maximum (all options exercised): <strong className="text-[#0A192F]">{fmt(devGroup.totals.fiveYearAllOptions)}</strong> · Years 3–5 are <em>District options, not firm commitments</em>.
            </p>
          </div>
        </div>

        {/* === ROI / Compared-to-What === */}
        <div data-testid="nh-budget-roi" className="bg-[#F9F7F1] border border-[#CBD5E1] p-7 lg:p-10 mb-12">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <TrendingUp className="h-7 w-7 text-[#8B0000] mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                The District Board Financial Pitch
              </div>
              <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F] leading-tight">
                Compared to <em className="italic">what?</em>
              </h3>
              <p className="text-sm text-[#475569] mt-4 leading-relaxed">
                The entire system breaks down to roughly{" "}
                <strong className="text-[#0A192F]">${nhSystemTotals.costPerHousehold.toLocaleString()} per household per year</strong> in
                Year 1 — about <strong className="text-[#0A192F]">$240 a month</strong> to wrap a family or senior in intensive case management with a fully-staffed Central Coordination Unit supervising every agency.
              </p>
            </div>
            <ul className="lg:col-span-8 space-y-4">
              {nhTriPortfolioROI.map((r, i) => (
                <li key={r.label} data-testid={`nh-roi-row-${i}`} className={`pl-5 border-l-2 ${r.highlight ? "border-[#8B0000]" : "border-[#CBD5E1]"}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <span className={`font-display text-lg leading-tight ${r.highlight ? "text-[#8B0000]" : "text-[#0A192F]"}`}>
                      {r.label}
                    </span>
                    <span className="font-mono text-sm text-[#0A192F] whitespace-nowrap">{r.value}</span>
                  </div>
                  <p className="text-xs text-[#475569] mt-1 leading-relaxed">{r.note}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* === Workforce + Cost-Avoidance narrative cards === */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          <div data-testid="nh-econ-workforce" className="bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-8">
            <Users className="h-7 w-7 text-[#D4AF37] mb-4" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Workforce Generation</div>
            <h4 className="font-display text-xl lg:text-2xl mb-3">
              80 corridor graduates pay for the program.
            </h4>
            <p className="text-sm text-[#CBD5E1] leading-relaxed">
              If Agency 2 alone routes 80 single parents from underemployment into stable, living-wage Amazon / FedEx / Sysco / Panelmatic roles or Lone Star North Harris allied-health certificates, the newly generated local spending and corridor tax base{" "}
              <strong className="text-[#F9F7F1]">easily covers the operational cost</strong> — with surplus.
            </p>
          </div>
          <div data-testid="nh-econ-cost-avoidance" className="bg-[#8B0000] text-[#F9F7F1] p-7 lg:p-8">
            <ShieldCheck className="h-7 w-7 text-[#D4AF37] mb-4" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Municipal Cost Avoidance</div>
            <h4 className="font-display text-xl lg:text-2xl mb-3">
              400 seniors aging in place cut the EMS bill.
            </h4>
            <p className="text-sm text-[#F9F7F1]/90 leading-relaxed">
              Keeping 400 district seniors safely independent in their own homes dramatically cuts the corridor&apos;s emergency-medical-services dispatch burden and avoids public-homelessness crises — line items that already cost the region more than ${nhSystemTotals.costPerHousehold.toLocaleString()} per household, every year, with no exit strategy.
            </p>
          </div>
        </div>

        {/* === 5-Year Forecast Table === */}
        <div data-testid="nh-budget-5yr-forecast" className="bg-[#F9F7F1] border border-[#CBD5E1] mb-12">
          <div className="px-6 lg:px-8 py-5 border-b border-[#CBD5E1] flex items-baseline justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                5-Year Forecast · 3% Annual Cost-of-Living Raise
              </div>
              <div className="font-display text-xl text-[#0A192F]">
                Disciplined growth. Predictable unit economics.
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">5-Year Recurring · All-In</div>
              <div className="font-display text-2xl text-[#0A192F]">{fmt(nhFiveYearGrandTotal)} <span className="text-base text-[#8B0000]">/ {fmt(nhFiveYearAllInTotal)}</span></div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white border-b border-[#CBD5E1]">
                  <th className="text-left px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">Year</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">Hub</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">Per Agency</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">3 Agencies</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Recurring Total</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">Dev Group</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">All-In Total</th>
                  <th className="text-right px-4 lg:px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-[#475569]">$ / HH</th>
                </tr>
              </thead>
              <tbody>
                {nhFiveYearForecast.map((y, i) => (
                  <tr key={y.year} data-testid={`nh-forecast-row-${i}`} className={`border-b border-[#E2E8F0] ${i === 0 ? "bg-[#EBE6D8]" : "bg-white"}`}>
                    <td className="px-4 lg:px-6 py-4 font-display text-base text-[#0A192F]">{y.year}</td>
                    <td className="px-4 lg:px-6 py-4 text-right text-[#334155]">{fmt(y.hub)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right text-[#334155]">{fmt(y.perAgency)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right text-[#334155]">{fmt(y.totalAgencies)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right font-display text-base text-[#8B0000]">{fmt(y.system)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right text-[#334155]">{fmt(y.devGroup)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right font-display text-base text-[#0A192F]">{fmt(y.systemAllIn)}</td>
                    <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#475569]">${y.costPerHH.toLocaleString()}</td>
                  </tr>
                ))}
                <tr className="bg-[#0A192F] text-[#F9F7F1]">
                  <td className="px-4 lg:px-6 py-4 font-display text-base">5-Year Total</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">—</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">—</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">—</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-display text-base text-[#D4AF37]">{fmt(nhFiveYearGrandTotal)}</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">{fmt(devGroup.totals.fiveYearAllOptions)}</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-display text-base text-[#D4AF37]">{fmt(nhFiveYearAllInTotal)}</td>
                  <td className="px-4 lg:px-6 py-4 text-right font-mono text-xs text-[#CBD5E1]">—</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="px-6 lg:px-8 py-4 border-t border-[#CBD5E1] font-mono text-[10px] uppercase tracking-wider text-[#64748B] leading-relaxed">
            Assumptions — 3% annual cost-of-living raise on all salaried positions (Program Architect, System Auditor, LCSW Program Manager, Combined Track Supervisor, Service Connectors, Intake Specialists). Non-salary operating costs (software, evaluation, admin, office ops) held flat for conservatism. Caseload cap maintained at 1:{nhSystemTotals.caseload} across the entire 5-year horizon.
          </p>
        </div>

        {/* === Governance & Conflict-of-Interest === */}
        <div data-testid="nh-governance-callout" className="bg-[#F9F7F1] border-l-4 border-[#8B0000] p-6 lg:p-8 mb-12">
          <div className="flex items-start gap-4">
            <ScrollText className="h-6 w-6 text-[#8B0000] shrink-0 mt-1" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-2">
                Governance &amp; Conflict-of-Interest Disclosure
              </div>
              <h4 className="font-display text-xl lg:text-2xl text-[#0A192F] leading-tight mb-3">
                The architect of the model does not operate one of the spokes.
              </h4>
              <p className="text-sm text-[#334155] leading-relaxed">
                The Founding Program Architect is awarded as a sole-source District professional-services contract to the program creator, with a written
                <strong className="text-[#0A192F]"> non-compete on all three Spoke RFPs </strong>
                for the full contract term plus a 12-month cool-down. The Architect serves the evaluation committee in an
                <strong className="text-[#0A192F]"> advisory, non-voting </strong>
                capacity. This structure — separating system design from agency operation — is the institutional safeguard that allows the District to engage the model&apos;s creator without compromising the integrity of the competitive RFP process.
              </p>
            </div>
          </div>
        </div>

        {/* Final Ask CTA */}
        <div className="bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-9 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
              The Five-Year Ask
            </div>
            <div className="font-display text-2xl lg:text-3xl">
              {fmt(nhSystemTotals.grandTotalAllIn)} Year 1 → {fmt(nhFiveYearAllInTotal)} total over 5 years.
              <span className="text-[#D4AF37] italic"> Annual review per agency.</span>
            </div>
            <p className="text-[#CBD5E1] text-sm mt-2 max-w-2xl">
              Includes Year-1 Sterile Mate Developer Group buildout ({fmt(devGroup.totals.year1)}) that declines to advisory-only by Year 4. Recurring 5-year cost of the Hub + 3 Agencies: <strong className="text-[#F9F7F1]">{fmt(nhFiveYearGrandTotal)}</strong>. Standardized data platform, third-party annual evaluation, hard caseload caps at 1:{nhSystemTotals.caseload}, LCSW clinical leadership at every agency.
            </p>
          </div>
          <a
            href="#contact"
            data-testid="nh-budget-cta"
            className="inline-flex items-center gap-2 bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] h-12 px-6 font-mono text-xs uppercase tracking-widest whitespace-nowrap"
          >
            Schedule a Budget Walk-Through <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>

        {/* Document footer · audit trail */}
        <p data-testid="nh-budget-audit-footer" className="mt-6 font-mono text-[10px] uppercase tracking-widest text-[#64748B] text-center leading-relaxed">
          Document version: <strong className="text-[#0A192F]">Proposal v1.0</strong> · Last revised {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })} · Not a binding offer · Subject to negotiation with North Houston District leadership
        </p>
      </div>
    </section>
  );
}

// ============ GATE (password-protected) ============
export default function BudgetGateNH() {
  const [unlocked, setUnlocked] = useState(() => {
    if (typeof window !== "undefined") {
      return window.localStorage.getItem(STORAGE_KEY) === "1";
    }
    return false;
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/financials/access`, { email, password });
      if (data.granted) {
        window.localStorage.setItem(STORAGE_KEY, "1");
        setUnlocked(true);
        toast.success("Access granted. NH Proposal 02 financial detail unlocked.");
      } else {
        toast.error("Access denied. Please contact George Broughton III for credentials.");
      }
    } catch (err) {
      toast.error("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (unlocked) return <NHBudgetUnlocked />;

  return (
    <section id="budget" data-testid="section-budget-nh-gated" className="bg-[#F9F7F1] py-24 lg:py-32 border-y border-[#CBD5E1]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <Lock className="h-10 w-10 text-[#8B0000] mx-auto mb-6" />
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000] mb-3">
          Restricted · Proposal 02 · North Houston District Budget Detail
        </div>
        <h2 className="font-display text-3xl sm:text-4xl text-[#0A192F] mb-4">
          The detailed budget is for credentialed reviewers only.
        </h2>
        <p className="text-[#334155] leading-relaxed mb-8 max-w-2xl mx-auto">
          The full 5-year budget — including line-item allocation, per-agency staffing detail (FTEs &amp; salaries), Developer Group taper, ROI math, and governance disclosure for the North Houston District deployment (1,200 households · ~$3.46M Year-1) — is available behind credentials. Public summary numbers are shown elsewhere on this page. Contact George Broughton III directly for access.
        </p>
        <form onSubmit={onSubmit} data-testid="budget-nh-form" className="max-w-md mx-auto text-left space-y-4">
          <div>
            <Label htmlFor="nh-email" className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Email</Label>
            <Input id="nh-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@northhouston.org" className="rounded-none mt-1.5 h-11" />
          </div>
          <div>
            <Label htmlFor="nh-pw" className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">Access Password</Label>
            <Input id="nh-pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-none mt-1.5 h-11" />
          </div>
          <Button type="submit" disabled={loading} data-testid="budget-nh-submit" className="rounded-none w-full bg-[#0A192F] hover:bg-[#1F2937] text-[#F9F7F1] h-11 font-mono text-xs uppercase tracking-widest">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Unlock NH Budget Detail <ArrowRight className="ml-2 h-4 w-4" /></>)}
          </Button>
          <div className="flex items-center gap-2 text-xs text-[#64748B] justify-center pt-2">
            <ShieldCheck className="h-3.5 w-3.5" /> Access is logged for audit purposes
          </div>
        </form>
      </div>
    </section>
  );
}
